import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { HomeHeader } from "@/components/home/HomeHeader";
import { HomeHero } from "@/components/home/HomeHero";
import { HomeBand } from "@/components/home/HomeBand";
import { HomeProblem } from "@/components/home/HomeProblem";
import { HomeScore } from "@/components/home/HomeScore";
import { HomeLiveConditions } from "@/components/home/HomeLiveConditions";
import { HomeTrust } from "@/components/home/HomeTrust";
import { HomeCoverage } from "@/components/home/HomeCoverage";
import { HomeReviews } from "@/components/home/HomeReviews";
import { HomeClosing } from "@/components/home/HomeClosing";
import { HomeFooter } from "@/components/home/HomeFooter";
import { HomeStickyCta } from "@/components/home/HomeStickyCta";
import { useHomeCounts, roundedTotal } from "@/hooks/useHomeCounts";

/**
 * Public marketing homepage for signed-out visitors at `/`.
 *
 * Owns its own `data-prerender-ready` flag (see PrerenderReady's self-managed
 * list). The flag is set once the counts query settles, success or failure, so
 * the prerender build can never hang on a broken RPC.
 */
export default function Home() {
  const { counts, settled } = useHomeCounts();
  // Copy claims Southern California, so it must count only the SoCal regions.
  const total = counts ? roundedTotal(counts.southern_california) : null;

  useEffect(() => {
    if (!settled) return;
    const id = window.requestAnimationFrame(() => {
      document.body.setAttribute("data-prerender-ready", "true");
    });
    return () => window.cancelAnimationFrame(id);
  }, [settled]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Coffee shops to work from, scored on WiFi and outlets"
        description="Find laptop friendly coffee shops in Los Angeles, Orange County, San Diego and Long Beach. Every café scored on free WiFi, outlets, seating and parking before you go."
        path="/"
        image="https://groundedtheapp.com/og-home.jpg"
        imageWidth={1200}
        imageHeight={630}
        imageType="image/jpeg"
      />
      <a
        href="#main"
        className="sr-only rounded-md bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
      >
        Skip to content
      </a>
      <HomeHeader />
      <main id="main">
        <HomeHero roundedTotal={total} />
        <HomeBand />
        <HomeProblem />
        <HomeScore />
        <HomeLiveConditions />
        <HomeTrust />
        <HomeCoverage counts={counts} />
        <HomeReviews />
        <HomeClosing />
      </main>
      <HomeFooter />
      {/* Reserves the sticky bar's height on phones so it never covers the footer. */}
      <div aria-hidden="true" className="h-[76px] md:hidden" />
      <HomeStickyCta />
    </div>
  );
}
